import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import useStore from '../stores/store';
import { AnimatePresence, motion } from 'framer-motion';
import usePersistStore from '../stores/persistStore';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Tooltip,
    css
} from '@chakra-ui/react';
import {
    ChevronDownIcon,
    ChevronOpen,
    ChevronRightIcon,
    CloseCircleIcon,
    DotsIcons,
    MotionBox,
    RefreshIcon,
    UpdateIcon
} from './UiElements';
import { Button, IconButton } from '@chakra-ui/button';
import {
    closeTabs,
    openNewGroupFromFolder
} from '../../platformFiles/browserAPI';
import { slimScrollStyles, tooltipDownAnimation } from './customTheme';

const OpenGroupButton = ({ groupData }) => {
    //    const buttonTextOpen = { light: '#003B13', dark: '#003B13' };
    let allTabsUrls = groupData.tabs.reduce((acc, tab) => {
        if (tab?.isStacked) {
            let stackedUrls = tab.stackedItems
                .map((item) => item.url)
                .filter((url) => url !== '' && url !== undefined);
            return [...acc, ...stackedUrls];
        } else if (tab.url !== '' && tab.url !== undefined) {
            return [...acc, tab.url];
        }
        return acc;
    }, []);

    let numberOfsites = allTabsUrls.length;
    const buttonText = useColorModeValue('gray.700', '#B0B7C1');
    const allActiveTabs = useStore((state) => state.allActiveTabs);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isExactMatch, setIsExactMatch] = useState(false);
    const [openGroupData, setOpenGroupData] = useState(null);

    const [extraUrlsInGroup, setExtraUrlsInGroup] = useState([]);
    const [removedUrls, setRemovedUrls] = useState([]);

    const determineMatchingUrls = (matchingGroup) => {
        if (matchingGroup && matchingGroup.children && groupData.tabs) {
            const matchingUrls = matchingGroup.children.filter((child) =>
                groupData.tabs.some((tab) => tab.url === child.url)
            );

            const extraUrlsInGroup = matchingGroup.children.filter(
                (child) =>
                    child.url &&
                    !groupData.tabs.some((tab) => tab.url === child.url)
            );
            const removedUrls = groupData.tabs.filter(
                (tab) =>
                    tab.url &&
                    !matchingGroup.children.some(
                        (child) => child.url === tab.url
                    )
            );

            if (
                matchingUrls.length === numberOfsites &&
                matchingUrls.length === matchingGroup.children.length &&
                removedUrls.length === 0
            ) {
                setIsExactMatch(true);
            } else {
                setIsExactMatch(false);
            }

            setExtraUrlsInGroup(extraUrlsInGroup);
            setRemovedUrls(removedUrls);
        }
    };

    const groups = useMemo(() => {
        return Object.values(allActiveTabs).reduce((acc, curr) => {
            const folders = curr.activeTabs.filter(
                (tab) => tab.contentType === 'folder'
            );
            return [...acc, ...folders];
        }, []);
    }, [allActiveTabs]);

    const matchingGroup = useMemo(() => {
        return groups.find((group) => group.title === groupData.title);
    }, [groups, groupData.title]);

    useEffect(() => {
        if (matchingGroup) {
            setOpenGroupData(matchingGroup);
            setIsOpen(true);
            setIsLoading(false);
            determineMatchingUrls(matchingGroup);
        } else {
            setIsOpen(false);
        }
    }, [matchingGroup]);

    const openGroup = () => {
        setIsLoading(true);
        //TODO take stacked items into account here...or not!!
        openNewGroupFromFolder(groupData, null, null /* window.id + '' */);
    };

    const updateGroup = () => {
        useStore
            .getState()
            .syncGroupSites(groupData, extraUrlsInGroup, removedUrls);
    };

    const closeGroup = () => {
        setIsLoading(true);
        let tabIds = [];
        const { children } = openGroupData;
        const windowId = useStore.getState().lastFocusedWindow;

        children.forEach((tab) => {
            tabIds.push(Number(tab.id));
        });
        closeTabs(tabIds, windowId, true);
    };

    const buttonBgOpen = useColorModeValue('#C9F7DA', '#8FDB99');
    const buttonTextOpen = useColorModeValue('#003B13', '#003B13');
    const hoverBgOpen = useColorModeValue('#C0EED1', '#7CCA86');
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');

    const menuProps = {
        bg: { menuBg },
        p: '8px 16px 8px 8px',
        rounded: '8px',
        fontWeight: 600,
        transition: '0.1s',
        //margin: '4px',
        _hover: { bg: lightBg }
    };

    return (
        <>
            {!isOpen ? (
                <Tooltip
                    rounded="6px"
                    label={numberOfsites === 0 ? 'No sites inside' : 'Open all'}
                    motionProps={tooltipDownAnimation}
                >
                    <Button
                        mt="4px"
                        rounded="6px"
                        //color="secondaryText"
                        color={buttonText}
                        p="4px 6px 4px 10px"
                        //w="68px"
                        onClick={openGroup}
                        fontWeight={600}
                        fontSize="14px"
                        height="24px"
                        w="min-content"
                        isDisabled={numberOfsites === 0}
                    >
                        {numberOfsites +
                            (numberOfsites === 1 ? ' site' : ' sites')}
                        <ChevronRightIcon />
                    </Button>
                </Tooltip>
            ) : (
                <Menu isLazy>
                    {({ isOpen }) => (
                        <>
                            <MenuButton
                                as={Button}
                                bg={buttonBgOpen}
                                isDisabled={isLoading}
                                p="4px 6px 4px 10px"
                                transition="0s"
                                fontSize="14px"
                                height="24px"
                                w="min-content"
                                mt="4px"
                                color={buttonTextOpen}
                                _hover={{ bg: hoverBgOpen }}
                                _active={{ bg: hoverBgOpen }}
                                display={'flex'}
                                flexWrap={'nowrap'}
                            >
                                <span
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'nowrap'
                                    }}
                                >
                                    {numberOfsites +
                                        (numberOfsites === 1
                                            ? ' site'
                                            : ' sites')}
                                    <ChevronDownIcon
                                        m="1px 0 0 2px"
                                        color={buttonTextOpen}
                                    />
                                </span>
                            </MenuButton>
                            <AnimatePresence>
                                {isOpen && (
                                    <MenuList
                                        zIndex={1000}
                                        bg={menuBg}
                                        border="none"
                                        boxShadow={
                                            'var(--chakra-colors-popoverShadowLight)'
                                        }
                                        rounded="10px"
                                        transition="0s"
                                        p="4px"
                                        as={motion.div}
                                        initial={{ y: -6, opacity: 0.4 }}
                                        animate={{
                                            y: 0,
                                            opacity: 1,
                                            transition: {
                                                duration: 0.1,
                                                ease: [0.17, 0.67, 0.83, 0.67]
                                            }
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => updateGroup()}
                                            fontSize="14px"
                                            {...menuProps}
                                            isDisabled={isExactMatch}
                                            icon={<UpdateIcon />}
                                        >
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap'
                                                }}
                                            >
                                                Update group
                                                {removedUrls.length > 0 && (
                                                    <Text
                                                        ml="auto"
                                                        letterSpacing="1.2px"
                                                        color="red.500"
                                                    >
                                                        -{removedUrls.length}
                                                    </Text>
                                                )}
                                                {extraUrlsInGroup.length >
                                                    0 && (
                                                    <Text
                                                        ml={
                                                            removedUrls.length >
                                                            0
                                                                ? '4px'
                                                                : 'auto'
                                                        }
                                                        letterSpacing="1.2px"
                                                        color="green.500"
                                                    >
                                                        +
                                                        {
                                                            extraUrlsInGroup.length
                                                        }
                                                    </Text>
                                                )}
                                            </span>
                                        </MenuItem>
                                        <MenuItem
                                            fontSize="14px"
                                            {...menuProps}
                                            onClick={() => closeGroup()}
                                            icon={<CloseCircleIcon />}
                                        >
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap'
                                                }}
                                            >
                                                Close group
                                                <Text
                                                    ml="auto"
                                                    opacity="0.6"
                                                    letterSpacing="1.2px"
                                                >
                                                    {
                                                        openGroupData?.children
                                                            ?.length
                                                    }
                                                </Text>
                                            </span>
                                        </MenuItem>
                                    </MenuList>
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </Menu>
            )}
        </>
    );
};

export default OpenGroupButton;
